"use client";

import { t } from "@/lib/i18n";
import { useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Shield,
  Megaphone,
  Users,
  KeyRound,
  LayoutDashboard,
  ArrowLeftRight,
  Gauge,
} from "lucide-react";
import { canAccessAdminPath } from "@/lib/admin-access";

const ITEMS = [
  { label: "Admin Home", path: "/admin", icon: LayoutDashboard },
  { label: "Broadcast", path: "/admin/broadcast", icon: Megaphone },
  { label: "RBAC", path: "/admin/rbac", icon: KeyRound },
  { label: "User Management", path: "/admin/users", icon: Users },
  { label: "SO Fast Track", path: "/admin/so-fast-track", icon: Gauge },
];

import { useStoredRbacAccess } from "@/lib/use-rbac-access";

export default function AdminSidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { permissions, roles } = useStoredRbacAccess();

  const visibleItems = useMemo(
    () =>
      ITEMS.filter((item) => {
        return canAccessAdminPath(item.path, permissions, roles);
      }),
    [permissions, roles],
  );

  return (
    <aside className="relative h-full w-[260px] border-r border-orange-200/60 bg-gradient-to-b from-orange-50/80 via-white/90 to-amber-50/70 backdrop-blur-2xl text-slate-700 aibas-app-font flex flex-col justify-between select-none">
      <div>
        <div className="px-5 py-5 border-b border-orange-200/50 bg-white/40">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-tr from-orange-500 to-amber-500 text-white shadow-sm shadow-orange-500/30">
              <Shield size={15} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-black leading-none">
                {t("Command Hub")}
              </p>
              <h2 className="text-base font-black text-slate-900 tracking-tight mt-0.5">
                {t("Control Center")}
              </h2>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1.5">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active =
              item.path === "/admin"
                ? pathname === item.path
                : pathname === item.path || pathname.startsWith(`${item.path}/`);
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => router.push(item.path)}
                className={`w-full text-left flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 group ${
                  active
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25 scale-[1.02]"
                    : "text-slate-600 hover:bg-orange-100/60 hover:text-orange-900"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`p-1.5 rounded-lg transition-colors ${
                      active
                        ? "bg-white/20 text-white"
                        : "bg-white text-orange-500 shadow-xs border border-orange-100 group-hover:bg-orange-500 group-hover:text-white"
                    }`}
                  >
                    <Icon size={15} />
                  </span>
                  <span>{t(item.label)}</span>
                </div>
                {active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="border-t border-orange-200/40 bg-white/30 p-4">
        <button
          type="button"
          onClick={() => router.push("/crm")}
          className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-[#E5722A] to-[#CC5F1E] px-5 py-3 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[#E5722A] focus:ring-offset-2 active:translate-y-0"
        >
          <ArrowLeftRight size={20} />
          <span>{t("Back to CRM")}</span>
        </button>
      </div>
    </aside>
  );
}
